import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sortBy } from 'lodash';
import { H5 } from '../../atoms';
import { NavigationItem, NavigationHeader } from '../../molecules';
import { spaceShape } from '../../../propsShape';
import { useDeleteShareParticipant } from '../../../features/chats/hooks/useDeleteShareParticipant';
import { NavigationItemActionsDropdown } from '../../molecules/NavigationItemActionsDropdown';

const NavigationContainer = styled.nav`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  row-gap: 24px;
`;
const NavigationSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const NavigationSectionWithSubSections = styled(NavigationSection)`
  row-gap: 20px;
`;
const NavigationSectionGroup = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  row-gap: 8px;
`;
const GroupHeader = styled(H5)`
  padding: 0 8px;
`;
const StyledNavigationItem = styled(NavigationItem)`
  max-width: 100%;

  /* Ensure agentic chats are properly visible */
  ${({ $isAgentic }) =>
    $isAgentic &&
    `
    & > div {
      color: var(--content-body) !important;
    }
    & span {
      color: var(--content-body) !important;
    }
  `}
`;

const renderActions = (
  conversation,
  currentUser,
  handleDeleteConversation,
  handleLeaveConversation
) =>
  function renderDropdownActions(toggleEditing) {
    return (
      <NavigationItemActionsDropdown
        onRenameConversation={() => toggleEditing(true)}
        onDeleteConversation={() =>
          conversation.shares?.[0]?.createdBy !== currentUser?.id && conversation.isShared
            ? handleLeaveConversation(conversation)
            : handleDeleteConversation(conversation)
        }
        actionLabel={
          conversation.shares?.[0]?.createdBy !== currentUser?.id && conversation.isShared
            ? 'Leave'
            : 'Delete'
        }
      />
    );
  };

const Navigation = ({
  groupedConversations,
  deleteConversation,
  updateConversation,
  openNextConversation,
  className = null,
  spaces = null,
  isReadOnly = false,
  currentUser = null,
  isAgentic = false
}) => {
  const deleteShareParticipant = useDeleteShareParticipant();

  const handleRenameConversation = (newTitle, id) => {
    if (!newTitle) return;
    updateConversation({ title: newTitle }, id);
  };

  const handleLeaveConversation = async (conversation) => {
    const isSharedConversation = conversation.shares?.[0];
    const isShareCreator = isSharedConversation?.createdBy === currentUser?.id;

    if (isSharedConversation && !isShareCreator) {
      // Find the participant that matches the current user
      const participant = conversation.shares[0].participants.find(
        (p) => p.userId === currentUser?.id
      );
      await deleteShareParticipant(participant.id, 'conversation', conversation.id);
      await deleteConversation(conversation.id, true);
      openNextConversation(conversation.id);
    }
  };

  const handleDeleteConversation = async (conversation) => {
    await deleteConversation(conversation.id);
    openNextConversation(conversation.id);
  };

  const chatUrlPrefix = isAgentic ? '/agentic-chats' : '/chats';
  const chatLabel = isAgentic ? 'Agentic Chats' : 'Chats';
  const newChatUrl = `${chatUrlPrefix}/new`;

  return (
    <NavigationContainer className={className}>
      <NavigationHeader label='Spaces' url='/spaces/new' isReadOnly={isReadOnly}>
        <NavigationSection>
          {sortBy(spaces || [], 'createdAt').map((space) => (
            <NavigationItem
              key={space.id}
              url={`/spaces/${space.id}`}
              label={space.title}
              isShared={space.isShared}
            />
          ))}
        </NavigationSection>
      </NavigationHeader>
      <NavigationHeader label={chatLabel} url={newChatUrl} isReadOnly={isReadOnly}>
        <NavigationSectionWithSubSections>
          {Object.entries(groupedConversations).map(([groupName, conversations]) => (
            <NavigationSectionGroup key={`${groupName}-${conversations.length}`}>
              <GroupHeader color='var(--content-subtitle)'>{groupName}</GroupHeader>
              <NavigationSection>
                {conversations.map((conversation) => (
                  <StyledNavigationItem
                    key={conversation.id}
                    url={`${chatUrlPrefix}/${conversation.id}`}
                    label={conversation.displayTitle}
                    isShared={conversation.isShared}
                    onCompleteEditing={(value) => handleRenameConversation(value, conversation.id)}
                    actions={renderActions(
                      conversation,
                      currentUser,
                      handleDeleteConversation,
                      handleLeaveConversation
                    )}
                    $isAgentic={isAgentic}
                  />
                ))}
              </NavigationSection>
            </NavigationSectionGroup>
          ))}
        </NavigationSectionWithSubSections>
      </NavigationHeader>
    </NavigationContainer>
  );
};

Navigation.propTypes = {
  className: PropTypes.string,
  groupedConversations: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayTitle: PropTypes.string.isRequired,
        shares: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            createdBy: PropTypes.string.isRequired,
            participants: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                userId: PropTypes.string.isRequired
              })
            )
          })
        )
      })
    )
  }).isRequired,
  spaces: PropTypes.arrayOf(spaceShape),
  isReadOnly: PropTypes.bool,
  deleteConversation: PropTypes.func.isRequired,
  updateConversation: PropTypes.func.isRequired,
  openNextConversation: PropTypes.func.isRequired,
  currentConversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }),
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }),
  isAgentic: PropTypes.bool
};

export { Navigation };
